import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, NAV } from "@/lib/content";
import { Wordmark } from "./Logo";

const TopBar = () => (
  <div className="bg-[#154F49] text-[#FBF8F1] text-xs sm:text-[13px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
      <p className="hidden md:flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5 text-[#CDA34F]" /> {COMPANY.address}
        <span className="text-[#CDA34F]">•</span> Serving {COMPANY.area} (confirm coverage directly)
      </p>
      <p className="md:hidden text-[#EDF1E6]">Upper Marlboro, MD • MD &amp; D.C. Metro</p>
      <div className="flex items-center gap-4">
        <a data-testid="topbar-email-link" href={`mailto:${COMPANY.email}`} className="hidden sm:flex items-center gap-1.5 hover:text-[#CDA34F] transition-colors">
          <Mail className="w-3.5 h-3.5" /> {COMPANY.email}
        </a>
        <a data-testid="topbar-phone-link" href={COMPANY.phoneHref} className="flex items-center gap-1.5 py-1.5 -my-1.5 font-semibold text-[#E2C372] hover:underline">
          <Phone className="w-3.5 h-3.5" /> Call {COMPANY.phoneDisplay}
        </a>
      </div>
    </div>
  </div>
);

export const Header = ({ onOpenChat }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
    <div className="no-print"><TopBar /></div>
    <header className="sticky top-0 z-40 no-print" data-testid="site-header">
      <div className="bg-[#FBF8F1]/90 backdrop-blur-xl border-b border-[#154F49]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between">
          <a href="#top" data-testid="header-home-link" aria-label="Elevion home"><Wordmark /></a>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} data-testid={`nav-${n.href.slice(1)}`} className="nav-link text-sm font-medium text-[#1A2E2B]">{n.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex"><a href="#request-care" data-testid="header-request-care-btn" className="btn-primary whitespace-nowrap">Request Care</a></span>
            <button type="button" data-testid="mobile-menu-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((v) => !v)} className="lg:hidden w-11 h-11 rounded-full border border-[#154F49]/20 flex items-center justify-center text-[#154F49]">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-y-auto max-h-[calc(100dvh-64px)] border-t border-[#154F49]/10" data-lenis-prevent data-testid="mobile-menu">
              <div className="px-4 py-4 space-y-1">
                {[...NAV, { href: "#apply-online", label: "Employment Application" }].map((n) => (
                  <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block px-4 py-3 rounded-xl text-base font-semibold text-[#154F49] hover:bg-[#EDF1E6]">{n.label}</a>
                ))}
                <button type="button" data-testid="mobile-open-chat" onClick={() => { setOpen(false); onOpenChat(); }} className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-base font-semibold text-[#154F49] bg-[#EDF1E6]">
                  <MessageCircle className="w-4 h-4" /> AI Assistant &amp; Support
                </button>
        <a href="#request-care" onClick={() => setOpen(false)} className="block text-center px-4 py-3.5 rounded-xl bg-[#CDA34F] text-[#154F49] font-bold text-sm" data-testid="mobile-request-care">Request Care</a>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <a href={COMPANY.phoneHref} className="text-center px-4 py-3.5 rounded-xl bg-[#154F49] text-[#FBF8F1] font-bold text-sm">Call {COMPANY.phoneDisplay}</a>
                  <a href={`mailto:${COMPANY.email}`} className="text-center px-4 py-3.5 rounded-xl bg-[#EDF1E6] text-[#154F49] border border-[#154F49]/25 font-bold text-sm">Email Us</a>
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
    </>
  );
};
